import React, {useState, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {AppCardTitle} from '../../../Presentation/components/ui/Typography';
import Card from '../../../Presentation/components/ui/Card';
import SerialsTrackingTable, {SerialItem} from './SerialsTrackingTable';
import SerialDetailsModal from './SerialDetailsModal';
import Button from '../../../Presentation/components/ui/Button';
import {Search, RefreshCw, Hash} from 'lucide-react';
import {PAGINATION_CONFIG} from '../../../constants/appConfig';
import {useNotification} from '../../../Services/useNotification';
import {LoggerService} from '../../../Services/common/LoggerService';
import {useInventory} from '../hooks/useInventory';
import {useDebounce} from '../../../Presentation/hooks/useDebounce';
import {
  INVENTORY_INPUT_CLASS,
  INVENTORY_ERROR_BOX_CLASS
} from '../ui/inventoryUi';

const SerialsTrackingTab: React.FC = () => {
  // Use inventory hook for real data
  const {getTrackedSerials, getSerialStatistics, deleteSerial} = useInventory();
  const {success: showSuccess, error: showError} = useNotification();
  const navigate = useNavigate();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(PAGINATION_CONFIG.DEFAULT_PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Data state
  const [serials, setSerials] = useState<SerialItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingSerialId, setDeletingSerialId] = useState<string | null>(null);
  const [detailsSerial, setDetailsSerial] = useState<SerialItem | null>(null);

  // Statistics state
  const [statistics, setStatistics] = useState<{
    totalSerials: number;
    availableSerials: number;
    assignedSerials: number;
    soldSerials: number;
  }>({
    totalSerials: 0,
    availableSerials: 0,
    assignedSerials: 0,
    soldSerials: 0
  });
  const [statisticsLoading, setStatisticsLoading] = useState<boolean>(false);

  // Filter state
  const [searchText, setSearchText] = useState<string>('');
  const debouncedSearch = useDebounce(searchText, 300);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [locationFilter, setLocationFilter] = useState<string>('');

  // Load statistics data
  const loadStatistics = async () => {
    try {
      setStatisticsLoading(true);
      const stats = await getSerialStatistics();
      setStatistics(stats);
    } catch (err) {
      LoggerService.error('Failed to load serial statistics:', err, 'SerialsTrackingTab');
      // Don't show error for statistics as it's not critical
    } finally {
      setStatisticsLoading(false);
    }
  };

  // Load serials data
  const loadSerials = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build filters for API call
      const filters: {
        productId?: string;
        locationId?: string;
        status?: string;
        page?: number;
        limit?: number;
        search?: string;
      } = {
        page: currentPage,
        limit: pageSize
      };
      if (statusFilter) {
        filters.status = statusFilter;
      }
      if (locationFilter) {
        filters.locationId = locationFilter;
      }
      if (debouncedSearch.trim()) {
        filters.search = debouncedSearch.trim();
      }

      // Get real data from API
      const result = await getTrackedSerials(filters);

      // Map to SerialItem format for the table
      const mappedSerials: SerialItem[] = result.items.map(serial => ({
        id: serial.id,
        serialNumber: serial.serialNumber,
        productName: serial.productName || `منتج ${serial.productId}` || 'منتج غير محدد',
        productId: serial.productId,
        locationName: serial.locationName || 'موقع غير محدد',
        locationId: serial.locationId,
        status: mapApiStatusToSerialItemStatus(serial.status),
        statusText: getStatusText(serial.status),
        createdAt: serial.createdAt,
        assignedTo: serial.assignedTo
      }));

      setSerials(mappedSerials);

      // Update pagination info
      if (result.pagination) {
        setTotalPages(result.pagination.totalPages || 1);
        setTotalCount(result.pagination.total || mappedSerials.length);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'فشل في جلب البيانات';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to map API status to SerialItem status
  const mapApiStatusToSerialItemStatus = (status: string): 'available' | 'assigned' | 'sold' => {
    switch (status.toUpperCase()) {
      case 'AVAILABLE':
        return 'available';
      case 'ASSIGNED':
        return 'assigned';
      case 'SOLD':
        return 'sold';
      default:
        return 'available';
    }
  };

  // Helper function to get status text in Arabic
  const getStatusText = (status: string): string => {
    switch (status.toUpperCase()) {
      case 'AVAILABLE':
        return 'متاح';
      case 'ASSIGNED':
        return 'مخصص للمسوق';
      case 'SOLD':
        return 'مباع';
      default:
        return 'متاح';
    }
  };

  // Use serials directly from API (backend pagination)
  const paginatedSerials = serials;

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Handle delete serial
  const handleDeleteSerial = async (id: string): Promise<boolean> => {
    setDeletingSerialId(id);
    try {
      // Use real API call
      await deleteSerial(id);

      // Remove from local state
      setSerials(prev => prev.filter(serial => serial.id !== id));

      showSuccess('تم حذف السيريال بنجاح');
      return true;
    } catch (error) {
      // Error notification is handled by StandardizedErrorHandler in the API layer
      const errorMessage = error instanceof Error ? error.message : 'فشل في حذف السيريال';
      showError(errorMessage);
      return false;
    } finally {
      setDeletingSerialId(null);
    }
  };

  // Navigate to Movements tab with serial filter (same flow as Sales "عرض في حركة المخزون")
  const handleTrackSerial = (serial: SerialItem) => {
    navigate(`/dashboard/inventory?tab=movements&serial=${encodeURIComponent(serial.serialNumber)}`);
  };

  const handleViewDetails = (serial: SerialItem) => {
    setDetailsSerial(serial);
  };

  // Load data on mount and when pagination/filters change
  useEffect(() => {
    loadSerials();
    loadStatistics();
  }, [currentPage, pageSize, statusFilter, locationFilter, debouncedSearch]);

  // Handle filter changes with page reset
  const handleSearchChange = (value: string) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setSearchText('');
    setStatusFilter('');
    setLocationFilter('');
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const handleLocationFilterChange = (value: string) => {
    setLocationFilter(value);
    setCurrentPage(1);
  };

  return (
    <div className="space-y-6">
      {/* Filters Card - header row matches InventoryMovementPage table card */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Hash className="w-5 h-5 text-brand-500" />
            <AppCardTitle>تتبع الأرقام التسلسلية</AppCardTitle>
          </div>
          <Button
            variant="outline"
            onClick={() => { loadSerials(); loadStatistics(); }}
            disabled={loading || statisticsLoading}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${(loading || statisticsLoading) ? 'animate-spin' : ''}`} />
            تحديث
          </Button>
        </div>
        <div className="flex flex-wrap gap-4 items-end">
          {/* Search on the left (RTL: first/rightmost) */}
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">البحث</label>
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={searchText}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="البحث بالرقم التسلسلي أو المنتج..."
                className={`${INVENTORY_INPUT_CLASS} pl-3 pr-9`}
              />
            </div>
          </div>
          {/* Status Filter */}
          <div className="min-w-[160px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">الحالة</label>
            <select
              value={statusFilter}
              onChange={(e) => handleStatusFilterChange(e.target.value)}
              className={INVENTORY_INPUT_CLASS}
            >
              <option value="">جميع الحالات</option>
              <option value="available">متاح</option>
              <option value="assigned">مخصص للمسوق</option>
              <option value="sold">مباع</option>
            </select>
          </div>
          {/* Location Filter */}
          <div className="min-w-[160px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">الموقع</label>
            <input
              type="text"
              value={locationFilter}
              onChange={(e) => handleLocationFilterChange(e.target.value)}
              placeholder="فلترة بالموقع..."
              className={INVENTORY_INPUT_CLASS}
            />
          </div>
          <Button variant="outline" onClick={handleResetFilters} className="shrink-0 self-end">
            مسح التصفية
          </Button>
        </div>
      </Card>

      {/* Error Display */}
      {error && (
        <div className={INVENTORY_ERROR_BOX_CLASS}>
          <div className="text-red-700 text-sm">{error}</div>
        </div>
      )}

      {/* Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="p-4">
          <div className="text-center">
            {statisticsLoading ? (
              <div className="animate-pulse">
                <div className="h-8 bg-gray-200 rounded w-16 mx-auto mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-24 mx-auto"></div>
              </div>
            ) : (
              <>
                <div className="text-2xl font-bold text-brand-600">{statistics.totalSerials}</div>
                <div className="text-sm text-gray-600">إجمالي السيريالات</div>
              </>
            )}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-center">
            {statisticsLoading ? (
              <div className="animate-pulse">
                <div className="h-8 bg-gray-200 rounded w-12 mx-auto mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-8 mx-auto"></div>
              </div>
            ) : (
              <>
                <div className="text-2xl font-bold text-green-600">{statistics.availableSerials}</div>
                <div className="text-sm text-gray-600">متاح</div>
              </>
            )}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-center">
            {statisticsLoading ? (
              <div className="animate-pulse">
                <div className="h-8 bg-gray-200 rounded w-8 mx-auto mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-20 mx-auto"></div>
              </div>
            ) : (
              <>
                <div className="text-2xl font-bold text-brand-600">{statistics.assignedSerials}</div>
                <div className="text-sm text-gray-600">مخصص للمسوق</div>
              </>
            )}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-center">
            {statisticsLoading ? (
              <div className="animate-pulse">
                <div className="h-8 bg-gray-200 rounded w-8 mx-auto mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-8 mx-auto"></div>
              </div>
            ) : (
              <>
                <div className="text-2xl font-bold text-purple-600">{statistics.soldSerials}</div>
                <div className="text-sm text-gray-600">مباع</div>
              </>
            )}
          </div>
        </Card>
      </div>

      {/* Table - uses AppTable built-in loading (TableLoadingState) */}
      <Card className="overflow-hidden">
        <SerialsTrackingTable
          data={paginatedSerials}
          page={currentPage}
          pageSize={pageSize}
          totalCount={totalCount}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onViewDetails={handleViewDetails}
          onDeleteSerial={handleDeleteSerial}
          onTrackSerial={handleTrackSerial}
          deletingSerialId={deletingSerialId}
          showPageSizeSelector={true}
          showPaginationInfo={true}
          exportable={true}
          loading={loading}
        />
      </Card>

      <SerialDetailsModal
        open={!!detailsSerial}
        serial={detailsSerial}
        onClose={() => setDetailsSerial(null)}
        onViewInMovements={handleTrackSerial}
      />
    </div>
  );
};

export default SerialsTrackingTab;
